import React, { useState } from 'react';
import { Button, Modal } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { getGlobalApiUrl } from '../../utils/url';
import { getAuthToken, getCompanySessionId } from '../../utils/storage';

export interface CompanySwitchData {
  user_id: number;
  old_company_id: number;
  new_company_id: number;
  old_company_name: string;
  new_company_name: string;
  message: string;
  user_name: string;
  timestamp: string;
}

interface EnhancedTenantSwitchModalProps {
  isVisible: boolean;
  switchData: CompanySwitchData;
  tabId: string;
}

const EnhancedTenantSwitchModal: React.FC<EnhancedTenantSwitchModalProps> = ({
  isVisible,
  switchData,
  tabId
}) => {
  const [btnLoader, setButtonLoader] = useState(false);

  const handleRefresh = async () => {
    setButtonLoader(true);
    try {
      const userAuth = getAuthToken();
      const sessionId = getCompanySessionId();

      if (tabId && userAuth && sessionId) {
        const clearUrl = getGlobalApiUrl('/api/v1/broadcast/clear/');
        const response = await fetch(`${clearUrl}?session_id=${sessionId}&tab_id=${tabId}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userAuth}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.warn('Failed to clear broadcast messages:', response.status);
        }
      }
    } catch (error) {
      console.error('Error clearing broadcast messages:', error);
    }

    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    setButtonLoader(false);

    if (typeof window === 'undefined') {
      return;
    }

    if (path.startsWith('/clients/nda-forms')) {
      window.location.href = '/clients/nda-forms';
      return;
    }

    if (path.startsWith('/clients/awaiting-approval')) {
      window.location.href = '/clients/awaiting-approval';
      return;
    }

    const nonProfilePrefixes = [
      '/clients/add',
      '/clients/create-new-customer',
      '/clients/Unauthorized'
    ];

    if (path.startsWith('/clients/') && !nonProfilePrefixes.some((prefix) => path.startsWith(prefix))) {
      window.location.href = '/companies';
      return;
    }

    window.location.reload();
  };

  const handleCancel = () => {
    // Modal is intentionally non-dismissible
  };

  return (
    <Modal
      title={
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-0">Tenant Changed</h3>
        </div>
      }
      open={isVisible}
      onCancel={handleCancel}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={500}
      className="tenant-switch-modal"
    >
      <div className="text-center py-6">
        <div className="mb-6">
          <p className="text-gray-700 text-base leading-relaxed">
            Hi <strong>{switchData.user_name}</strong>, You have changed your tenant from{' '}
            <strong>&quot;{switchData.old_company_name}&quot;</strong> to{' '}
            <strong>&quot;{switchData.new_company_name}&quot;</strong>.
          </p>
        </div>

        <Button
          type="primary"
          size="large"
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={btnLoader}
          className="bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700 px-8 py-2 h-12 text-base font-medium"
        >
          Refresh Now
        </Button>
      </div>
    </Modal>
  );
};

export default EnhancedTenantSwitchModal;
